import { useCallback, useEffect, useRef, useState } from 'react'
import ReactBeforeSliderComponent from 'react-before-after-slider-component'

type DemoImage = {
  imageUrl: string
  alt?: string
}

const DEMONSTRATION_DELAY = 500
const DEFAULT_DURATION = 800

type Animation = {
  start: number
  end: number
  duration: number
}

const START_POSITION = 55
const END_POSITION_1 = 80
const END_POSITION_2 = 35

const ANIMATIONS: Animation[] = [
  {
    start: START_POSITION,
    end: END_POSITION_1,
    duration: DEFAULT_DURATION,
  },
  // pause
  {
    start: END_POSITION_1,
    end: END_POSITION_1,
    duration: 30,
  },
  {
    start: END_POSITION_1,
    end: END_POSITION_2,
    duration: DEFAULT_DURATION,
  },
]

type SliderProps = {
  originalImage: DemoImage
  restyledImage: DemoImage
  showLabels?: boolean
  originalLabel?: string
  restyledLabel?: string
  className?: string
}

const BeforeAfterSlider: React.FC<SliderProps> = ({
  originalImage,
  restyledImage,
  showLabels = true,
  originalLabel = 'Original Room',
  restyledLabel = 'Restyled Room',
  className,
}) => {
  const [delimiterPercentPosition, setDelimiterPercentPosition] =
    useState<number>(START_POSITION)
  const allAnimationsRef = useRef<Animation[]>([])
  const animationStartTimeRef = useRef<number | null>(null)
  const animationPositionsRef = useRef<Animation | null>(null)

  // Fade thresholds - where fading starts and ends
  const FADE_START = 35 // Start fading when 65% visible (100 - 65 = 35)
  const FADE_END = 25 // Completely faded at 75% hidden

  // Calculate opacity values for each label based on slider position
  const originalLabelOpacity = calculateOpacity(
    delimiterPercentPosition,
    FADE_END,
    FADE_START
  )
  const restyledLabelOpacity = calculateOpacity(
    100 - delimiterPercentPosition,
    FADE_END,
    FADE_START
  )

  // Function to calculate opacity based on current position
  function calculateOpacity(
    position: number,
    min: number,
    max: number
  ): number {
    if (position <= min) return 0
    if (position >= max) return 1
    return (position - min) / (max - min)
  }

  const animate = useCallback((timestamp: number) => {
    let animationPositions = animationPositionsRef.current

    if (!animationPositions) {
      const currentAnimation = allAnimationsRef.current.shift()
      if (!currentAnimation) {
        return
      }
      animationPositions = animationPositionsRef.current = currentAnimation
    }

    let animationStartTime = animationStartTimeRef.current
    if (!animationStartTime) {
      animationStartTime = animationStartTimeRef.current = timestamp
    }

    const {
      start: animationStartPosition,
      end: animationEndPosition,
      duration: animationDuration,
    } = animationPositions

    if (timestamp > animationStartTime + animationDuration) {
      // End of animation
      setDelimiterPercentPosition(animationEndPosition)
      animationPositionsRef.current = null
      animationStartTimeRef.current = null
    } else {
      const animationPhase =
        (timestamp - animationStartTime) / animationDuration
      const coordinatesDifference =
        (animationEndPosition - animationStartPosition) *
        timePhaseToCoordinadeDifferenceCoefficient(animationPhase)

      setDelimiterPercentPosition(
        animationStartPosition + coordinatesDifference
      )
    }

    window.requestAnimationFrame(animate)
  }, [])

  useEffect(() => {
    allAnimationsRef.current = [...ANIMATIONS]
    const timeout = setTimeout(
      () => window.requestAnimationFrame(animate),
      DEMONSTRATION_DELAY
    )
    return () => clearTimeout(timeout)
  }, [animate])

  return (
    <div className='relative'>
      {showLabels && originalLabelOpacity > 0 && (
        <div
          className='absolute left-2 top-3 z-10 rounded-full bg-black/20 px-2 py-1 text-xs font-semibold text-white/80 shadow-md backdrop-blur transition-opacity duration-300 sm:left-4 sm:top-4 md:text-sm'
          style={{ opacity: originalLabelOpacity }}
        >
          {originalLabel}
        </div>
      )}

      {showLabels && restyledLabelOpacity > 0 && (
        <div
          className='absolute right-2 top-3 z-10 rounded-full bg-black/20 px-2 py-1 text-xs font-semibold text-white/80 shadow-md backdrop-blur transition-opacity duration-300 sm:right-4 sm:top-4 md:text-sm'
          style={{ opacity: restyledLabelOpacity }}
        >
          {restyledLabel}
        </div>
      )}

      {originalImage && restyledImage && (
        <ReactBeforeSliderComponent
          firstImage={restyledImage}
          secondImage={originalImage}
          currentPercentPosition={delimiterPercentPosition}
          onChangePercentPosition={setDelimiterPercentPosition}
          className={className}
        />
      )}
    </div>
  )
}

function timePhaseToCoordinadeDifferenceCoefficient(x: number) {
  return (Math.sin(x * Math.PI - Math.PI / 2) + 1) / 2
}

export default BeforeAfterSlider
